import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, X, ArrowRight, Sparkles, Layers, ClipboardList } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatRelativeTime, formatFileSize } from '@/lib/utils';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui';
import { useUIStore, useDocumentsStore, useAuthStore } from '@/stores';
import { invokeWithRetry } from '@/lib/supabase';
import type { Document } from '@/types';

interface SemanticResult {
  id: string;
  documentId: string;
  documentTitle: string;
  content: string;
  pageNumber: number | null;
  timestampStart: number | null;
  timestampEnd: number | null;
  metadata: Record<string, unknown> | null;
  similarity: number;
}

interface SearchResponse {
  results: SemanticResult[];
}

type ResultItem =
  | { type: 'document'; data: Document }
  | { type: 'semantic'; data: SemanticResult };

export function SearchModal() {
  const navigate = useNavigate();
  const { searchOpen, closeSearch } = useUIStore();
  const { documents } = useDocumentsStore();
  const { user } = useAuthStore();

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [semanticResults, setSemanticResults] = useState<SemanticResult[]>([]);
  const [semanticLoading, setSemanticLoading] = useState(false);
  const [semanticError, setSemanticError] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Tier 1: Client-side document title filtering
  const documentResults = useMemo(() => {
    if (!query.trim()) {
      return documents.filter(d => d.status === 'indexed').slice(0, 5);
    }
    const q = query.toLowerCase();
    return documents
      .filter(doc => doc.title.toLowerCase().includes(q))
      .slice(0, 6);
  }, [query, documents]);

  // Combined flat result list for keyboard navigation
  const allResults: ResultItem[] = useMemo(() => {
    const items: ResultItem[] = documentResults.map(d => ({ type: 'document' as const, data: d }));
    items.push(...semanticResults.map(s => ({ type: 'semantic' as const, data: s })));
    return items;
  }, [documentResults, semanticResults]);

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Open/close effects
  useEffect(() => {
    if (searchOpen) {
      setIsAnimating(true);
      setTimeout(() => inputRef.current?.focus(), 50);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [searchOpen]);

  // Escape key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && searchOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchOpen]);

  // Tier 2: Debounced semantic search
  useEffect(() => {
    if (query.trim().length < 3) {
      setSemanticResults([]);
      setSemanticError(null);
      setSemanticLoading(false);
      return;
    }

    setSemanticLoading(true);
    const timer = setTimeout(async () => {
      try {
        const response = await invokeWithRetry<SearchResponse>('search', {
          query: query.trim(),
          limit: 5,
        });

        if (response.error) {
          setSemanticError('Content search unavailable');
          setSemanticResults([]);
        } else if (response.data) {
          setSemanticResults(response.data.results);
          setSemanticError(null);
        }
      } catch {
        setSemanticError('Content search unavailable');
        setSemanticResults([]);
      } finally {
        setSemanticLoading(false);
      }
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [query, user?.id]);

  // Scroll active item into view
  useEffect(() => {
    const activeEl = listRef.current?.querySelector('[data-active="true"]');
    activeEl?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleClose = useCallback(() => {
    setIsAnimating(false);
    setTimeout(() => {
      closeSearch();
      setQuery('');
      setSemanticResults([]);
      setSemanticError(null);
      setSemanticLoading(false);
      setActiveIndex(0);
    }, 150);
  }, [closeSearch]);

  const handleActivate = useCallback((index: number) => {
    const item = allResults[index];
    if (!item) return;

    if (item.type === 'document') {
      navigate(`/reader/${item.data.id}`);
    } else {
      navigate(`/reader/${item.data.documentId}`);
    }
    handleClose();
  }, [allResults, navigate, handleClose]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const totalItems = allResults.length;
    if (totalItems === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, totalItems - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleActivate(activeIndex);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      handleClose();
    }
  };

  if (!searchOpen) return null;

  const showSemanticSection = query.trim().length >= 3;
  const hasDocResults = documentResults.length > 0;
  const hasSemanticResults = semanticResults.length > 0;

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className={cn(
        'fixed inset-0 z-50 flex items-start justify-center pt-[15vh]',
        'bg-black/60 backdrop-blur-sm',
        'transition-opacity duration-150',
        isAnimating ? 'opacity-100' : 'opacity-0'
      )}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search knowledge base"
        className={cn(
          'w-full max-w-xl mx-4',
          'bg-background-card border border-border-subtle rounded-2xl shadow-2xl',
          'flex flex-col max-h-[70vh]',
          'transition-all duration-150',
          isAnimating
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-95 translate-y-4'
        )}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border-subtle">
          <Search size={18} className="text-foreground-muted flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search documents and content..."
            className="flex-1 bg-transparent text-sm text-foreground-primary placeholder:text-foreground-muted outline-none"
          />
          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-xs text-foreground-muted bg-background-secondary border border-border-subtle rounded">
            Esc
          </kbd>
          <button
            onClick={handleClose}
            className="p-1 rounded-lg hover:bg-background-hover transition-colors text-foreground-muted hover:text-foreground-primary"
          >
            <X size={16} />
          </button>
        </div>

        {/* Results */}
        <div ref={listRef} className="overflow-y-auto flex-1 p-2">
          {/* Document results section */}
          <div className="px-2 py-1.5">
            <span className="text-xs font-semibold text-foreground-muted uppercase tracking-wide">
              {query.trim() ? 'Documents' : 'Recent'}
            </span>
          </div>

          {hasDocResults ? (
            documentResults.map((doc, i) => (
              <DocumentResultItem
                key={doc.id}
                doc={doc}
                isActive={activeIndex === i}
                onActivate={() => { navigate(`/reader/${doc.id}`); handleClose(); }}
                onAskAI={() => { navigate(`/ask?doc=${doc.id}`); handleClose(); }}
                onFlashcards={() => { navigate('/flashcards'); handleClose(); }}
                onQuiz={() => { navigate('/quizzes'); handleClose(); }}
              />
            ))
          ) : (
            <p className="px-4 py-3 text-sm text-foreground-muted">
              {query.trim() ? 'No documents match your search' : 'No indexed documents yet'}
            </p>
          )}

          {/* Semantic content results section */}
          {showSemanticSection && (
            <>
              <div className="mx-2 my-1 border-t border-border-subtle" />
              <div className="px-2 py-1.5">
                <span className="text-xs font-semibold text-foreground-muted uppercase tracking-wide">
                  Content Matches
                </span>
              </div>

              {semanticLoading && (
                <div className="space-y-1 px-2">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="flex items-start gap-3 px-2 py-3">
                      <Skeleton className="w-8 h-8 rounded-lg flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-3 w-1/3" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!semanticLoading && semanticError && (
                <p className="px-4 py-2 text-xs text-foreground-muted italic">
                  {semanticError}. Showing document matches only.
                </p>
              )}

              {!semanticLoading && !semanticError && hasSemanticResults && (
                semanticResults.map((result, i) => {
                  const globalIndex = documentResults.length + i;
                  return (
                    <SemanticResultItem
                      key={result.id}
                      result={result}
                      isActive={activeIndex === globalIndex}
                      onActivate={() => { navigate(`/reader/${result.documentId}`); handleClose(); }}
                    />
                  );
                })
              )}

              {!semanticLoading && !semanticError && !hasSemanticResults && (
                <p className="px-4 py-2 text-xs text-foreground-muted italic">
                  No content matches found
                </p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-border-subtle flex items-center justify-between text-xs text-foreground-muted">
          <div className="flex items-center gap-3">
            <span><kbd className="px-1 py-0.5 bg-background-secondary border border-border-subtle rounded text-[10px]">↑↓</kbd> navigate</span>
            <span><kbd className="px-1 py-0.5 bg-background-secondary border border-border-subtle rounded text-[10px]">↵</kbd> open</span>
            <span><kbd className="px-1 py-0.5 bg-background-secondary border border-border-subtle rounded text-[10px]">esc</kbd> close</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Document result row
function DocumentResultItem({
  doc,
  isActive,
  onActivate,
  onAskAI,
  onFlashcards,
  onQuiz,
}: {
  doc: Document;
  isActive: boolean;
  onActivate: () => void;
  onAskAI: () => void;
  onFlashcards: () => void;
  onQuiz: () => void;
}) {
  const typeIcons: Record<string, string> = {
    pdf: 'PDF',
    docx: 'DOCX',
    pptx: 'PPTX',
    image: 'IMG',
    audio: 'MP3',
    video: 'VID',
  };

  return (
    <button
      data-active={isActive}
      onClick={onActivate}
      className={cn(
        'group w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-lg transition-colors',
        isActive ? 'bg-background-hover' : 'hover:bg-background-hover'
      )}
    >
      <div className="w-8 h-8 rounded-lg bg-accent-primary/10 flex items-center justify-center flex-shrink-0">
        <FileText size={16} className="text-accent-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground-primary truncate">{doc.title}</p>
        <p className="text-xs text-foreground-muted">
          {typeIcons[doc.type] || doc.type.toUpperCase()} · {formatFileSize(doc.file_size)} · {formatRelativeTime(doc.created_at)}
        </p>
      </div>
      {doc.status === 'indexed' && (
        <div className={cn(
          'flex items-center gap-1 transition-opacity',
          isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        )}>
          <button
            onClick={(e) => { e.stopPropagation(); onAskAI(); }}
            title="Ask AI"
            className="p-1.5 rounded-md hover:bg-accent-primary/10 transition-colors"
          >
            <Sparkles size={14} className="text-accent-primary" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onFlashcards(); }}
            title="Flashcards"
            className="p-1.5 rounded-md hover:bg-accent-primary/10 transition-colors"
          >
            <Layers size={14} className="text-foreground-muted" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onQuiz(); }}
            title="Quiz"
            className="p-1.5 rounded-md hover:bg-accent-primary/10 transition-colors"
          >
            <ClipboardList size={14} className="text-foreground-muted" />
          </button>
        </div>
      )}
      {doc.status !== 'indexed' && (
        <Badge variant={doc.status === 'error' ? 'danger' : 'default'}>
          {doc.status}
        </Badge>
      )}
    </button>
  );
}

// Semantic content result row
function SemanticResultItem({
  result,
  isActive,
  onActivate,
}: {
  result: SemanticResult;
  isActive: boolean;
  onActivate: () => void;
}) {
  const snippet = result.content.length > 150
    ? result.content.slice(0, 150) + '...'
    : result.content;

  const location = result.pageNumber
    ? `p.${result.pageNumber}`
    : result.timestampStart
      ? formatTimestamp(result.timestampStart)
      : null;

  return (
    <button
      data-active={isActive}
      onClick={onActivate}
      className={cn(
        'w-full flex items-start gap-3 px-3 py-2.5 text-left rounded-lg transition-colors',
        isActive ? 'bg-background-hover' : 'hover:bg-background-hover'
      )}
    >
      <div className="w-8 h-8 rounded-lg bg-accent-primary/5 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Search size={14} className="text-foreground-muted" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-foreground-muted mb-0.5">
          {result.documentTitle}
          {location && <span> · {location}</span>}
        </p>
        <p className="text-sm text-foreground-primary line-clamp-2">{snippet}</p>
        <p className="text-xs text-foreground-muted mt-1">
          {Math.round(result.similarity * 100)}% match
        </p>
      </div>
      <ArrowRight size={14} className="text-foreground-muted flex-shrink-0 mt-1" />
    </button>
  );
}

function formatTimestamp(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
